import { isAxiosError } from 'axios';
import type { CollectionPlace } from '@/features/collection-place';
import type { CollectorContributionContext } from '@/lib/community-collector.storage';
import type { CreateAudioContributionWithFilesDto, CreateContributionDto, Question } from '@/types/theme.types';
import { rejectedIssues, type AudioQualityIssue } from './audio-quality';
import type { AudioQualityState } from './hooks/use-audio-quality';
import { questionLocalText } from './initial-data.helpers';
import type { AudioAnswer, TextAnswer } from './types';
import { isRecordingDeviceSelectionValid, recordingDeviceDetailsRequired, type RecordingDeviceSelection } from '@/features/recording-device-metadata';

/**
 * What the two initial-data sessions send, as pure functions: the API
 * payloads, the line remembered for the collector dashboard and how a failed
 * request is read. The hooks call these; initial-data.payloads.test.ts pins
 * them so a redesign cannot change what reaches the backend.
 */

export type SubmitResult = {
  ok: boolean;
  message?: string;
  /** Spec 006: measurable reasons the recording was refused. */
  issues?: AudioQualityIssue[];
};

type QuestionRef = Pick<Question, 'id' | 'textLocal' | 'textFr'>;

type Common = {
  sectorId: string;
  language: string;
  question: QuestionRef;
  collectorContext: CollectorContributionContext | null;
  collectionPlace: CollectionPlace | null;
};

/** POST /themes/contributions/audio (multipart), one spoken answer. */
export function audioContributionPayload({
  sectorId,
  language,
  question,
  collectorContext,
  collectionPlace,
  answer,
  quality,
  recordingDevice,
}: Common & { answer: AudioAnswer; quality: AudioQualityState; recordingDevice: RecordingDeviceSelection }): CreateAudioContributionWithFilesDto {
  if (!isRecordingDeviceSelectionValid(recordingDevice)) {
    throw new Error('Recording-device selection is incomplete');
  }
  return {
    sectorId,
    questionId: question.id,
    language,
    questionTextLocal: questionLocalText(question),
    responseAudioFile: answer.blob,
    recordingDurationMs: answer.durationMs,
    recordingDeviceCategory: recordingDevice.recordingDeviceCategory,
    recordingMicrophoneType: recordingDevice.recordingMicrophoneType,
    recordingDeviceDetails: recordingDeviceDetailsRequired(recordingDevice) ? recordingDevice.recordingDeviceDetails.trim() : undefined,
    communityProfileId: collectorContext?.communityProfileId,
    communityCode: collectorContext?.communityProfileCode,
    collectionPlace,
    audioQuality: quality.status === 'ready' ? quality.metrics : null,
  };
}

/** POST /themes/contributions, one written answer (stored with a placeholder audio URL). */
export function textContributionPayload({
  sectorId,
  language,
  question,
  collectorContext,
  collectionPlace,
  answer,
}: Common & { answer: TextAnswer }): CreateContributionDto {
  return {
    sectorId,
    questionId: question.id,
    language,
    questionTextLocal: answer.questionTranslation.trim() || questionLocalText(question),
    responseTextLocal: answer.response.trim(),
    responseAudioLocalUrl: `initial-text://${sectorId}/${question.id}`,
    recordingDurationMs: 0,
    communityProfileId: collectorContext?.communityProfileId,
    communityCode: collectorContext?.communityProfileCode,
    collectionPlace,
  };
}

/** The line rememberCollectorModuleContribution keeps after a spoken answer. */
export function collectorAudioEntry(question: QuestionRef) {
  return { type: 'audio' as const, content: questionLocalText(question) || `Question ${question.id}` };
}

/** The line rememberCollectorModuleContribution keeps after a written answer. */
export function collectorTextEntry(response: string) {
  return { type: 'questionnaire' as const, content: response.trim() };
}

/**
 * Whether a spoken answer can go: `null` when it can, else the result to
 * return without calling the API (nothing recorded yet, quality still being
 * measured, or measured issues).
 */
export function audioSubmitBlock(
  hasQuestion: boolean,
  answer: AudioAnswer | null,
  quality: AudioQualityState,
): SubmitResult | null {
  if (!hasQuestion || !answer || quality.status === 'measuring') return { ok: false };
  if (quality.status === 'ready' && quality.issues.length > 0) return { ok: false, issues: quality.issues };
  return null;
}

/**
 * A failed request: a 422 on a spoken answer carries the quality issues the
 * server measured; otherwise the server message, if any.
 */
export function submitErrorResult(error: unknown, readQualityIssues: boolean): SubmitResult {
  if (readQualityIssues && isAxiosError(error) && error.response?.status === 422) {
    return { ok: false, issues: rejectedIssues(error.response.data) };
  }
  const message = isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message : undefined;
  return { ok: false, message };
}
